import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { Dropdown, TextInput, ActionButton } from "colbi_web_ui/lib/components/inputs";
import { SovosIcon } from "mosaic-react";
import { HighlightOff } from "mosaic-react-icons";
import { UserRoles } from "../../../enums/userRoles";
import {
    associateExternalApplicationOrganization,
    createExternalApplicationCredentials,
    disassociateExternalApplicationOrganization,
    fetchAssignedOrganizationsForExternalApi,
    fetchExternalApiPackages,
    fetchExternalApplicationByEmail,
} from "../../actions/externalApiActions";
import styles from "./UserForm.module.sass";

const canManageExternalApi = (role) =>
    [UserRoles.ADMIN, UserRoles.SUPPORT, UserRoles.PP_SUPPORT].includes(role);

const ExternalApiCredentialsInner = ({ userId, email }) => {
    const { i18n, pushNotification } = useGlobalValue();
    const dispatch = useDispatch();

    const [credentialsEmail, setCredentialsEmail] = useState("");
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [packages, setPackages] = useState([]);
    const [orgOptions, setOrgOptions] = useState([]);
    const [associatedOrgs, setAssociatedOrgs] = useState([]);
    const [apiKey, setApiKey] = useState("");
    const [apiSecret, setApiSecret] = useState("");
    const [hasExisting, setHasExisting] = useState(false);
    const [loading, setLoading] = useState(false);
    const userIdRef = useRef(userId);
    const credentialsEmailRef = useRef(credentialsEmail);

    useEffect(() => {
        userIdRef.current = userId;
    }, [userId]);

    useEffect(() => {
        credentialsEmailRef.current = credentialsEmail;
    }, [credentialsEmail]);

    useEffect(() => {
        setCredentialsEmail("");
        setSelectedOrg(null);
        setSelectedPackage(null);
        setOrgOptions([]);
        setAssociatedOrgs([]);
        setApiKey("");
        setApiSecret("");
        setHasExisting(false);
        setLoading(false);
    }, [userId]);

    useEffect(() => {
        if (userId && email && !credentialsEmail) {
            setCredentialsEmail(String(email).trim());
        }
    }, [userId, email, credentialsEmail]);

    useEffect(() => {
        dispatch(fetchExternalApiPackages())
            .then((rows) => setPackages(rows || []))
            .catch(() => {
                setPackages([]);
                pushNotification &&
                    pushNotification({
                        open: true,
                        type: "error",
                        message: i18n`Failed to load External API packages`,
                        timeout: "short",
                    });
            });
    }, []);

    useEffect(() => {
        if (!userId) {
            setOrgOptions([]);
            return;
        }
        let cancelled = false;
        dispatch(fetchAssignedOrganizationsForExternalApi(userId))
            .then((rows) => {
                if (cancelled) {
                    return;
                }
                setOrgOptions(
                    (rows || []).map((org) => ({
                        id: String(org.id),
                        name: org.name || String(org.id),
                    })),
                );
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setOrgOptions([]);
                pushNotification &&
                    pushNotification({
                        open: true,
                        type: "error",
                        message: i18n`Failed to load organizations`,
                        timeout: "short",
                    });
            });
        return () => {
            cancelled = true;
        };
    }, [userId]);

    useEffect(() => {
        if (!credentialsEmail || !userId) {
            return;
        }
        let cancelled = false;
        setLoading(true);
        dispatch(fetchExternalApplicationByEmail(credentialsEmail))
            .then((app) => {
                if (cancelled) {
                    return;
                }
                if (!app) {
                    setHasExisting(false);
                    setApiKey("");
                    setApiSecret("");
                    setAssociatedOrgs([]);
                    setSelectedPackage(null);
                    return;
                }
                setHasExisting(true);
                setApiKey(app.apiKey || "");
                setApiSecret("");
                setAssociatedOrgs(
                    (app.organizationIds || []).map((orgId) => {
                        const match = orgOptions.find((o) => String(o.id) === String(orgId));
                        return match || { id: String(orgId), name: String(orgId) };
                    }),
                );
                if (app.packageId) {
                    setSelectedPackage({
                        id: String(app.packageId),
                        name: String(app.packageId),
                    });
                }
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                pushNotification &&
                    pushNotification({
                        open: true,
                        type: "error",
                        message: i18n`Failed to load External API credentials`,
                        timeout: "short",
                    });
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [credentialsEmail, userId]);

    useEffect(() => {
        if (!selectedPackage?.id || !packages.length) {
            return;
        }
        const match = packages.find((item) => String(item.id) === String(selectedPackage.id));
        if (match && match.name !== selectedPackage.name) {
            setSelectedPackage({ id: String(match.id), name: match.name });
        }
    }, [packages, selectedPackage]);

    useEffect(() => {
        if (!associatedOrgs.length || !orgOptions.length) {
            return;
        }
        const next = associatedOrgs.map((org) => {
            const match = orgOptions.find((o) => String(o.id) === String(org.id));
            return match && match.name !== org.name ? match : org;
        });
        const changed = next.some((org, idx) => org.name !== associatedOrgs[idx].name);
        if (changed) {
            setAssociatedOrgs(next);
        }
    }, [orgOptions, associatedOrgs]);

    if (!userId) {
        return (
            <div className={styles["user-management"]}>
                <h4 className={styles["title"]}>{i18n`External API`}</h4>
                <small>{i18n`Save the user before generating External API credentials`}</small>
            </div>
        );
    }

    const notifySuccess = (message) =>
        pushNotification &&
        pushNotification({ open: true, type: "confirmation", message, timeout: "short" });
    const notifyError = (message) =>
        pushNotification &&
        pushNotification({
            open: true,
            type: "error",
            message: message || i18n`Something went wrong`,
            timeout: "short",
        });

    const handleGenerate = () => {
        if (!credentialsEmail) {
            notifyError(i18n`Save the user before generating External API credentials`);
            return;
        }
        if (!selectedOrg?.id || !selectedPackage?.id) {
            notifyError(i18n`Select an organization and a package`);
            return;
        }
        const requestUserId = userId;
        const requestEmail = credentialsEmail;
        setLoading(true);
        dispatch(
            createExternalApplicationCredentials({
                email: requestEmail,
                packageId: selectedPackage.id,
                organizationIds: [selectedOrg.id],
                sourceUserId: requestUserId,
            }),
        )
            .then((result) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                setHasExisting(true);
                setApiKey(result.apiKey);
                setApiSecret(result.apiSecret);
                setAssociatedOrgs([
                    {
                        id: String(selectedOrg.id),
                        name: selectedOrg.name,
                    },
                ]);
                notifySuccess(i18n`External API credentials generated`);
            })
            .catch((err) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                notifyError(err.message);
            })
            .finally(() => {
                if (requestUserId === userIdRef.current) {
                    setLoading(false);
                }
            });
    };

    const handleAddOrg = () => {
        if (!selectedOrg?.id) {
            notifyError(i18n`Select an organization`);
            return;
        }
        if (associatedOrgs.some((org) => String(org.id) === String(selectedOrg.id))) {
            notifyError(i18n`Organization already associated`);
            return;
        }
        const requestUserId = userId;
        const requestEmail = credentialsEmail;
        setLoading(true);
        dispatch(
            associateExternalApplicationOrganization({
                email: requestEmail,
                organizationId: selectedOrg.id,
                sourceUserId: requestUserId,
            }),
        )
            .then((result) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                setAssociatedOrgs(
                    (result.organizationIds || []).map((orgId) => {
                        const match = orgOptions.find((o) => String(o.id) === String(orgId));
                        return match || { id: String(orgId), name: String(orgId) };
                    }),
                );
                notifySuccess(i18n`Organization associated to External API`);
            })
            .catch((err) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                notifyError(err.message);
            })
            .finally(() => {
                if (requestUserId === userIdRef.current) {
                    setLoading(false);
                }
            });
    };

    const handleRemoveOrg = (organizationId) => {
        if (associatedOrgs.length <= 1) {
            notifyError(i18n`Cannot remove the last organization`);
            return;
        }
        const requestUserId = userId;
        const requestEmail = credentialsEmail;
        setLoading(true);
        dispatch(
            disassociateExternalApplicationOrganization({
                email: requestEmail,
                organizationId,
                sourceUserId: requestUserId,
            }),
        )
            .then((result) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                setAssociatedOrgs(
                    (result.organizationIds || []).map((orgId) => {
                        const match = orgOptions.find((o) => String(o.id) === String(orgId));
                        return match || { id: String(orgId), name: String(orgId) };
                    }),
                );
                notifySuccess(i18n`Organization removed from External API`);
            })
            .catch((err) => {
                if (
                    requestUserId !== userIdRef.current ||
                    requestEmail !== credentialsEmailRef.current
                ) {
                    return;
                }
                notifyError(err.message);
            })
            .finally(() => {
                if (requestUserId === userIdRef.current) {
                    setLoading(false);
                }
            });
    };

    const copyValue = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            notifySuccess(i18n`Copied to clipboard`);
        } catch (err) {
            notifyError(err.message);
        }
    };

    return (
        <div className={styles["user-management"]}>
            <h4 className={styles["title"]}>{i18n`External API`}</h4>
            <small>
                {i18n`One API Key and Secret per user email. Organizations share the same credentials.`}
            </small>

            <div className={styles["input-field"]}>
                <h5 className={styles["label"]}>{i18n`Organization`}</h5>
                <Dropdown
                    className={styles["dropdown"]}
                    placeholder={i18n`Select organization`}
                    value={selectedOrg}
                    options={orgOptions}
                    filter
                    onChange={(e) => setSelectedOrg(e.target.value || null)}
                />
            </div>

            <div className={styles["input-field"]}>
                <h5 className={styles["label"]}>{i18n`Package`}</h5>
                <Dropdown
                    className={styles["dropdown"]}
                    placeholder={i18n`Select package`}
                    value={selectedPackage}
                    options={(packages || []).map((pkg) => ({
                        id: String(pkg.id),
                        name: pkg.name,
                    }))}
                    filter
                    disabled={hasExisting}
                    onChange={(e) => setSelectedPackage(e.target.value || null)}
                />
            </div>

            {!hasExisting && (
                <div className={styles["group-content"]}>
                    <ActionButton
                        primary
                        type="button"
                        disabled={loading || !selectedOrg || !selectedPackage || !credentialsEmail}
                        onClick={handleGenerate}
                    >
                        {i18n`Generate`}
                    </ActionButton>
                </div>
            )}

            {hasExisting && (
                <>
                    <div className={styles["group-content"]}>
                        <div className={styles["flex"]}>
                            <TextInput
                                className={styles["input"]}
                                name={i18n`API Key`}
                                label={i18n`API Key`}
                                value={apiKey}
                                disabled
                            />
                            <ActionButton type="button" onClick={() => copyValue(apiKey)}>
                                {i18n`Copy`}
                            </ActionButton>
                        </div>
                    </div>

                    {apiSecret ? (
                        <div className={styles["group-content"]}>
                            <div className={styles["flex"]}>
                                <TextInput
                                    className={styles["input"]}
                                    name={i18n`API Secret`}
                                    label={i18n`API Secret`}
                                    value={apiSecret}
                                    disabled
                                />
                                <ActionButton type="button" onClick={() => copyValue(apiSecret)}>
                                    {i18n`Copy`}
                                </ActionButton>
                            </div>
                            <small>
                                {i18n`Copy the API Secret now. It will not be shown again.`}
                            </small>
                        </div>
                    ) : null}

                    <div className={styles["input-field"]}>
                        <h5 className={styles["label"]}>{i18n`Associated organizations`}</h5>
                        {(associatedOrgs || []).map((org) => (
                            <div key={org.id} className={styles["project-entry"]}>
                                <div>{org.name}</div>
                                <div>
                                    <button
                                        type="button"
                                        className={styles["remove-button"]}
                                        onClick={() => handleRemoveOrg(org.id)}
                                        disabled={loading || associatedOrgs.length <= 1}
                                    >
                                        <SovosIcon>
                                            <HighlightOff />
                                        </SovosIcon>
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            className={styles["add-button"]}
                            type="button"
                            disabled={loading || !selectedOrg}
                            onClick={handleAddOrg}
                        >
                            <small>{i18n`[+] Add organization`}</small>
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

const ExternalApiCredentials = ({ userId, email }) => {
    const { user } = useGlobalValue();

    if (!canManageExternalApi(user?.role)) {
        return null;
    }

    return <ExternalApiCredentialsInner userId={userId} email={email} />;
};

export default ExternalApiCredentials;
